import React, { useState } from "react";
import {
	Box,
	Button,
	Divider,
	Flex,
	IconButton,
	Image,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	ModalBody,
	ModalFooter,
	Tag,
	Text,
	Tooltip,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { ClasswizeModal } from "@/components/ui/Modal";
import { colours, qoriaColours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { BlockAllowToggle } from "../ListForm/BlockAllowToggle";
import { formatWebsiteTarget, isQuickBlockList, isRuleInert } from "../utils";
import { List, RuleAction } from "../types";
import { RuleRelationshipTags, RuleSourceById } from "../RuleRelationshipTags";
import { OnOffToggle } from "./OnOffToggle";
import { InClassRule } from "./types";
import { inClassAudience } from "./utils";

interface Props {
	rule: InClassRule;
	libraryLists: List[];
	onChangeAction: (action: RuleAction) => void;
	onChangeEnabled: (enabled: boolean) => void;
	onAddToList: (listId: string) => void;
	onEdit: () => void;
	onDelete: () => void;
	/** Disables the row's Block/Allow and On/Off toggles while a rule write is in flight. */
	isUpdating?: boolean;
	/** Spins the row's delete control, and locks the row's other controls, while its delete is in flight. */
	isDeleting?: boolean;
	/** Spins the row's add-to-list control while its add is in flight. */
	isAddingToList?: boolean;
	ruleSourceById?: RuleSourceById;
}

export function InClassRuleRow(props: Props): JSX.Element {
	const {
		rule,
		libraryLists,
		onChangeAction,
		onChangeEnabled,
		onAddToList,
		onEdit,
		onDelete,
		isUpdating = false,
		isDeleting = false,
		isAddingToList = false,
		ruleSourceById = {},
	} = props;
	const { t } = useTranslation();
	const blockingRulesOnly = useClasswizeConfigStore((state) => state.data.create_blocking_rules_only) === true;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const getFavicon = useSignatureStore((state) => state.getFavicon);
	const findSignature = useSignatureStore((state) => state.findSignature);
	const findAppControl = useSignatureStore((state) => state.findAppControl);
	const classroomStudents = useSelectedClassroomStore((state) => state.students);

	const isSignature = rule.type === "SIGNATURE";
	const isAppControl = rule.type === "APP_CONTROL";
	const signature = findSignature(rule.target);
	const appControl = findAppControl(rule.target);
	const blocked = rule.action === "block";
	// App-control storage has no enabled flag, so Off is stored as allow. Website/signature leftover
	// allow rules still allow access, so they keep an Allow appearance instead of looking like Off.
	const isLeftoverAllow = blockingRulesOnly && !isAppControl && !blocked;
	const isOn = rule.enabled !== false && blocked;
	// An overridden or conflicted rule is greyed out to show it is not in effect. Its controls stay
	// live, because changing or deleting it is how the teacher resolves the clash.
	const isInert = isRuleInert(rule);
	const listsToAdd = libraryLists.filter((list) => !isQuickBlockList(list));
	const [pendingListId, setPendingListId] = useState<string | undefined>();

	let label = formatWebsiteTarget(rule.target);
	if (isAppControl) {
		label = appControl?.name ?? rule.target;
	} else if (isSignature) {
		label = lookupName(rule.target);
	}

	let favicon: string | undefined;
	if (isAppControl) {
		favicon = appControl?.favicon || undefined;
	} else if (isSignature && signature) {
		favicon = getFavicon(rule.target);
	}

	let iconColour = qoriaColours.brand.success[500];
	if (blocked) {
		iconColour = qoriaColours.brand.danger[500];
	}
	if (blockingRulesOnly && !isLeftoverAllow) {
		iconColour = qoriaColours.brand.neutral[300];
		if (isOn) {
			iconColour = qoriaColours.brand.danger[500];
		}
	}
	if (isInert) {
		iconColour = colours.form.disabled.text;
	}

	const audience = inClassAudience(
		rule,
		(classroomStudents ?? []).map((student) => ({
			id: student.stableIdV1,
			stableIdV1: student.stableIdV1,
			qoriaUserId: student.qoriaUserId,
			name: `${student.firstName} ${student.lastName}`.trim(),
		}))
	);

	let scopeLabel = t("rulesLibrary.activeRules.wholeClass");
	let scopeIcon = "fa-users";
	if (audience.kind === "one") {
		scopeLabel = audience.name;
		scopeIcon = "fa-user";
	} else if (audience.kind === "many") {
		scopeLabel = t("rulesLibrary.activeRules.audienceStudentCount", { count: audience.count });
	}

	function handleChooseList(listId: string): void {
		if (audience.kind === "class") {
			onAddToList(listId);
			return;
		}
		setPendingListId(listId);
	}

	function handleConfirmAddToList(): void {
		if (pendingListId === undefined) {
			return;
		}
		onAddToList(pendingListId);
		setPendingListId(undefined);
	}

	return (
		<>
			<Flex
				align="center"
				gap={2}
				pl={4}
				pr={2}
				py={2}
				border="1px solid"
				borderColor={qoriaColours.brand.neutral[100]}
				rounded="xl"
				bg={isInert ? qoriaColours.brand.neutral[100] : qoriaColours.brand.primary[10]}
				mb={3}
			>
				<Flex align="center" justify="center" boxSize={8} rounded="xl" bg={qoriaColours.brand.neutral[10]} boxShadow="sm" flexShrink={0}>
					{favicon ? (
						<Image src={favicon} alt="" boxSize={5} opacity={isInert ? 0.4 : 1} />
					) : (
						<ChakraFAIcon icon="fa-earth-americas" variant="solid" color={iconColour} boxSize={4} />
					)}
				</Flex>
				<Box flex={1} minW={0} ml={1}>
					<Text
						fontFamily={fonts.body}
						fontWeight={500}
						color={isInert ? colours.form.disabled.text : colours.text.title}
						isTruncated
						title={label}
					>
						{label}
					</Text>
					<Tag
						size="md"
						bg={qoriaColours.brand.neutral[10]}
						color={isInert ? colours.form.disabled.text : colours.text.regular}
						fontFamily={fonts.body}
						fontWeight={400}
						gap={1}
						isTruncated
						title={scopeLabel}
					>
						<ChakraFAIcon icon={scopeIcon} variant="solid" boxSize={3} />
						{scopeLabel}
					</Tag>
				</Box>
				<RuleRelationshipTags rules={[rule]} sourceByRuleId={ruleSourceById} />
				<Box width="180px" flexShrink={0} ml="auto">
					{blockingRulesOnly && !isLeftoverAllow ? (
						<OnOffToggle size="sm" value={isOn} onChange={onChangeEnabled} isDisabled={isUpdating || isDeleting} />
					) : (
						<BlockAllowToggle size="sm" value={rule.action} onChange={onChangeAction} isDisabled={isUpdating || isDeleting} />
					)}
				</Box>
				<Flex align="center" flexShrink={0}>
					{isAppControl ? (
						<Box boxSize={10} flexShrink={0} aria-hidden />
					) : (
						<Menu>
							<Tooltip label={t("rulesLibrary.activeRules.addToListTooltip")}>
								<MenuButton
									as={IconButton}
									aria-label={t("rulesLibrary.activeRules.addToListAria", { name: label })}
									variant="ghost"
									isDisabled={isAddingToList || isDeleting}
									isLoading={isAddingToList}
									p={0}
									icon={<ChakraFAIcon icon="fa-folder-plus" variant="regular" color={colours.text.regular} boxSize={4} />}
								/>
							</Tooltip>
							<MenuList>
								<Text px={3} py={2} fontFamily={fonts.body} fontWeight={500} color={colours.text.title}>
									{t("rulesLibrary.activeRules.addToList")}
								</Text>
								<Divider borderColor={qoriaColours.brand.neutral[200]} />
								{listsToAdd.length === 0 ? (
									<MenuItem isDisabled fontFamily={fonts.body}>
										{t("rulesLibrary.activeRules.noListsToAdd")}
									</MenuItem>
								) : (
									listsToAdd.map((list) => (
										<MenuItem
											key={list.id}
											fontFamily={fonts.body}
											icon={<ChakraFAIcon icon="fa-sitemap" variant="solid" color={qoriaColours.brand.primary[600]} boxSize={4} />}
											onClick={() => handleChooseList(list.id)}
										>
											{list.title}
										</MenuItem>
									))
								)}
							</MenuList>
						</Menu>
					)}
					<Tooltip label={t("rulesLibrary.activeRules.editInClassTooltip")}>
						<IconButton
							aria-label={t("rulesLibrary.activeRules.editInClassAria", { name: label })}
							variant="ghost"
							onClick={onEdit}
							isDisabled={isDeleting}
							icon={<ChakraFAIcon icon="fa-pen-to-square" variant="regular" color={colours.text.regular} boxSize={4} />}
							p={0}
						/>
					</Tooltip>
					<Tooltip label={t("rulesLibrary.activeRules.removeFromClassTooltip")}>
						<IconButton
							aria-label={t("rulesLibrary.form.deleteRuleAria", { target: label })}
							variant="ghost"
							onClick={onDelete}
							isLoading={isDeleting}
							icon={<ChakraFAIcon icon="fa-trash-can" variant="regular" color={qoriaColours.brand.danger[500]} boxSize={4} />}
							p={0}
						/>
					</Tooltip>
				</Flex>
			</Flex>
			<ClasswizeModal
				isOpen={pendingListId !== undefined}
				onClose={() => setPendingListId(undefined)}
				title={t("rulesLibrary.activeRules.addToListWholeClassTitle")}
				size="lg"
				dataTestId="rules-library-add-to-list-whole-class"
			>
				<ModalBody>
					<Text>{t("rulesLibrary.activeRules.addToListWholeClassBody", { audience: scopeLabel })}</Text>
				</ModalBody>
				<ModalFooter gap={2}>
					<Button variant="ghost" onClick={() => setPendingListId(undefined)}>
						{t("rulesLibrary.activeRules.cancel")}
					</Button>
					<Button onClick={handleConfirmAddToList}>{t("rulesLibrary.activeRules.addToList")}</Button>
				</ModalFooter>
			</ClasswizeModal>
		</>
	);
}
